import { useState, useEffect, useCallback, useRef } from "react";
import { supabase, isSupabaseConfigured, isCurrentUserAdmin } from "../lib/supabase";

/**
 * Manages the admin auth session using real Supabase Auth.
 *
 * There is no client-side admin password anywhere in this app. Signing in
 * calls supabase.auth.signInWithPassword, which authenticates against
 * Supabase's own user store and returns a real session/JWT. Whether that
 * signed-in user is actually an *admin* (as opposed to just any account) is
 * decided by a row in the `admin_users` table — checked here for UI purposes,
 * and enforced independently by Postgres Row Level Security on every write
 * (see supabase/migrations/002_admin_auth.sql). The client-side `isAdmin`
 * flag below only controls what the UI shows; it grants no actual access on
 * its own.
 */

// Guards against a hung network request (e.g. a stalled connection to
// Supabase) leaving the UI stuck on "Checking session..." or "Signing
// in..." forever.
//
// This needs to be generous. Browsers throttle timers and network activity
// in background tabs, so when the user switches away and comes back after a
// few minutes, Supabase's own token-refresh request (which fires on resume)
// can take much longer than it would in an active tab.
const ADMIN_CHECK_TIMEOUT_MS = 20000;

// Sign-out gets a much shorter leash than the checks above. The server-side
// token revoke is a nice-to-have; being returned to the login screen (and
// STAYING there) is not. If the revoke hasn't answered in a few seconds we
// stop waiting on it and clear local storage anyway — see signOut().
const SIGN_OUT_TIMEOUT_MS = 5000;

// A unique sentinel so callers can reliably detect a timeout regardless of
// what shape the underlying promise would have resolved to.
const TIMED_OUT = Symbol("timed-out");

const withTimeout = (promise, ms) =>
  Promise.race([
    promise,
    new Promise((resolve) => setTimeout(() => resolve(TIMED_OUT), ms)),
  ]);

const CONFIG_ERROR =
  "Database is not configured. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY.";

/**
 * Hard-clears any Supabase session that supabase-js may have left behind in
 * browser storage.
 *
 * THIS IS THE FIX FOR "logging out, closing the tab, and coming back still
 * logged in." supabase-js's signOut() does NOT throw when the server-side
 * revoke fails — it *resolves* with `{ error }`, and (see GoTrueClient
 * `_signOut`) it returns early on that error path WITHOUT calling
 * `_removeSession()`. So the token stays in localStorage. The old code
 * wrapped signOut() in try/catch, which never fires for a resolved promise,
 * so the failure was invisible: React state was cleared (the UI showed the
 * login form) while the persisted session survived untouched. Reopening
 * /admin then restored it via getSession() and the admin was "still logged
 * in".
 *
 * The same thing happens with no error at all if the tab is closed while the
 * revoke request is still in flight — the request is aborted and
 * `_removeSession()` is never reached.
 *
 * Clearing the storage key ourselves makes local sign-out unconditional and
 * independent of whether the network call succeeded, failed, or never
 * finished. Supabase stores the session under `sb-<project-ref>-auth-token`,
 * so we match on that shape rather than hardcoding a project ref.
 */
const purgeSupabaseAuthStorage = () => {
  if (typeof window === "undefined") return;

  const purge = (store) => {
    if (!store) return;
    try {
      const doomed = [];
      for (let i = 0; i < store.length; i += 1) {
        const key = store.key(i);
        // e.g. "sb-abcdefgh-auth-token", "sb-abcdefgh-auth-token-code-verifier"
        if (key && key.startsWith("sb-") && key.includes("auth-token")) {
          doomed.push(key);
        }
      }
      // Collect first, remove second — removing while iterating shifts indices.
      doomed.forEach((key) => store.removeItem(key));
    } catch (err) {
      // Private mode / storage disabled. Nothing else we can do here, and
      // there's nothing persisted to leak in that case either.
      console.warn("Could not clear Supabase auth storage:", err);
    }
  };

  purge(window.localStorage);
  purge(window.sessionStorage);
};

export const useAdminAuth = () => {
  const [session, setSession] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);
  const [sessionError, setSessionError] = useState("");
  // True while we have a session but haven't yet gotten a confirmed
  // true/false back from the is_admin() RPC. Kept separate from
  // checkingSession (which only covers restoring the session itself) so the
  // UI can show a "checking admin status" state instead of briefly flashing
  // the login form for a session that's actually valid.
  const [checkingAdminStatus, setCheckingAdminStatus] = useState(false);

  // isKnownAdminRef tracks whether we've already *confirmed* admin status
  // in this session, independent of React state timing. It lets a
  // recheck (e.g. after a token refresh on tab resume) distinguish "I
  // don't know yet" from "I definitely know this account isn't admin" —
  // only the latter should ever log someone out.
  const isKnownAdminRef = useRef(false);

  // De-dupes concurrent is_admin() RPCs. On every page load the hook fired
  // TWO of them: one from init() below, and one from the onAuthStateChange
  // listener, because supabase-js emits an INITIAL_SESSION event to each
  // newly-registered subscriber. Both round trips have to finish before the
  // page renders, and both queue on the same auth lock as the feature
  // queries — pure added latency on every refresh, for an answer that is
  // identical both times.
  const inFlightAdminCheckRef = useRef(null);

  const refreshAdminStatus = useCallback(async (isInitialCheck = false) => {
    if (!isSupabaseConfigured) {
      isKnownAdminRef.current = false;
      setIsAdmin(false);
      setCheckingAdminStatus(false);
      return false;
    }
    if (isInitialCheck) setCheckingAdminStatus(true);
    try {
      // Share a single in-flight RPC between concurrent callers.
      if (!inFlightAdminCheckRef.current) {
        inFlightAdminCheckRef.current = withTimeout(
          isCurrentUserAdmin(),
          ADMIN_CHECK_TIMEOUT_MS
        ).finally(() => {
          inFlightAdminCheckRef.current = null;
        });
      }
      const result = await inFlightAdminCheckRef.current;

      if (result === TIMED_OUT) {
        // We genuinely don't know the answer. Never treat "unknown" as
        // "not admin" for someone who was already confirmed admin — that
        // would log an already-verified admin out just because a
        // recheck (e.g. after a background-tab token refresh) was slow.
        // Real admin writes are independently enforced by RLS regardless
        // of this client-side flag, so leaving isAdmin as-is here is safe.
        console.warn("Admin status check timed out; keeping previous status.");
        return isKnownAdminRef.current;
      }

      // A confirmed answer (true or false) from the server is always
      // trusted, including a confirmed "false" — e.g. an admin was
      // actually removed from admin_users.
      isKnownAdminRef.current = result;
      setIsAdmin(result);
      return result;
    } catch (err) {
      console.error("Admin status check failed:", err);
      // A network/unexpected error is not a confirmed "not admin" either.
      // Only log out on the *initial* check for a fresh session (where we
      // have no prior confirmed status to fall back on); otherwise keep
      // whatever we already knew.
      if (isInitialCheck) {
        isKnownAdminRef.current = false;
        setIsAdmin(false);
        return false;
      }
      return isKnownAdminRef.current;
    } finally {
      setCheckingAdminStatus(false);
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    // No fallback: if Supabase isn't configured, there is no session to
    // check and no login that can ever succeed — surface that immediately
    // instead of attempting a request against a null/placeholder client.
    if (!isSupabaseConfigured) {
      setSessionError(CONFIG_ERROR);
      setCheckingSession(false);
      return;
    }

    const init = async () => {
      try {
        const result = await withTimeout(supabase.auth.getSession(), ADMIN_CHECK_TIMEOUT_MS);
        const restored = result === TIMED_OUT ? null : result.data.session;
        if (!mounted) return;
        setSession(restored);
        // Unblock the page as soon as we know whether a session exists.
        //
        // NOTE: contrary to what a comment here used to claim, getSession()
        // is NOT purely a local storage read. It awaits the client's
        // initializePromise (which runs _recoverAndRefresh) and, if the
        // stored access token is within EXPIRY_MARGIN of expiring, performs
        // a network POST to /auth/v1/token to refresh it before resolving.
        // Since Supabase access tokens default to a 1-hour life, any admin
        // returning after an hour pays for that round trip on load — and
        // while it's in flight it holds the auth lock, so every other
        // Supabase request (features, is_admin) queues behind it. That is a
        // real and unavoidable part of the refresh-time cost; the fix is to
        // not stack redundant requests on top of it, which is what the
        // de-duping above and in useFeatures.js does.
        if (mounted) setCheckingSession(false);
        if (restored) refreshAdminStatus(true);
      } catch (err) {
        console.error("Failed to restore session:", err);
        if (mounted) {
          setSessionError("Couldn't verify your session. Please try logging in again.");
          setCheckingSession(false);
        }
      }
    };

    init();

    const { data: listener } = supabase.auth.onAuthStateChange(async (event, newSession) => {
      if (!mounted) return;
      setSession(newSession);

      if (newSession) {
        // INITIAL_SESSION is emitted to every newly-registered subscriber
        // as soon as the client finishes initializing. It is not a login —
        // it's the same session init() above is already handling, so
        // reacting to it here just fires a second, redundant is_admin()
        // RPC on every single page load.
        if (event === "INITIAL_SESSION") {
          return;
        }
        // TOKEN_REFRESHED fires routinely, including right after the tab
        // regains visibility and Supabase silently refreshes the access
        // token. That's not a new login — it's proof the existing session
        // is still valid — so there's no need to re-verify admin status
        // (and definitely no reason it could ever log the user out).
        if (event === "TOKEN_REFRESHED" && isKnownAdminRef.current) {
          return;
        }
        await refreshAdminStatus();
      } else {
        // No session at all (real sign-out, or a refresh token that's
        // actually expired/revoked) is the only case that should clear
        // admin status.
        isKnownAdminRef.current = false;
        setIsAdmin(false);
      }
    });

    return () => {
      mounted = false;
      listener?.subscription?.unsubscribe();
    };
  }, [refreshAdminStatus]);

  const signIn = async (email, password) => {
    if (!isSupabaseConfigured) throw new Error(CONFIG_ERROR);

    const result = await withTimeout(
      supabase.auth.signInWithPassword({ email, password }),
      ADMIN_CHECK_TIMEOUT_MS
    );
    if (result === TIMED_OUT) {
      throw new Error("Sign-in timed out. Please check your connection and try again.");
    }
    const { data, error } = result;
    if (error) throw error;

    const admin = await refreshAdminStatus(true);
    if (!admin) {
      // Valid Supabase account, but not on the admin allow-list. Sign them
      // back out of the admin page session rather than leaving a
      // non-admin "logged in" with a UI that will just fail every action.
      // Use the same hardened path as the logout button so this can't leave
      // a stray session behind in storage either.
      await signOut();
      throw new Error(
        "This account isn't authorized as an admin. Ask an existing admin to add you."
      );
    }

    setSession(data.session);
    return true;
  };

  const signOut = async () => {
    // Clear local React state unconditionally, before anything that can fail,
    // so the UI returns to the login screen immediately.
    isKnownAdminRef.current = false;
    inFlightAdminCheckRef.current = null;
    setSession(null);
    setIsAdmin(false);

    if (isSupabaseConfigured) {
      try {
        // Attempt the server-side revoke, but never let it hold up (or
        // decide) the local sign-out. supabase-js RESOLVES with { error }
        // here rather than throwing when the revoke fails, and in that case
        // it skips clearing the stored session entirely — which is exactly
        // how a "logged out" admin ended up still logged in after reopening
        // the tab. We inspect the resolved value instead of relying on a
        // catch block that would never fire, and the storage purge below
        // runs either way.
        const result = await withTimeout(supabase.auth.signOut(), SIGN_OUT_TIMEOUT_MS);
        if (result === TIMED_OUT) {
          console.warn(
            "Server-side sign-out did not respond in time; clearing the local session anyway."
          );
        } else if (result?.error) {
          console.warn(
            "Server-side sign-out failed; clearing the local session anyway:",
            result.error
          );
        }
      } catch (err) {
        console.warn("Server-side sign-out threw; clearing the local session anyway:", err);
      }
    }

    // Unconditional and last: whatever happened above, make sure no Supabase
    // session survives in browser storage. Without this, closing the tab and
    // reopening /admin restores the old session and the admin appears to
    // still be logged in.
    purgeSupabaseAuthStorage();
  };

  return {
    session,
    isAdmin,
    isLoggedIn: !!session && isAdmin,
    checkingSession,
    checkingAdminStatus,
    sessionError,
    signIn,
    signOut,
  };
};
